import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import { Toaster } from "react-hot-toast";
import authService from "../services/authService";
import { useAuth } from "../../../context/AuthContext.js";
import api from "../../../services/api.js";
import AuthForm from "../components/AuthForm";
import PasswordStrengthIndicator from "../components/PasswordStrengthIndicator";
import Logo from "../../../common/assets/blue-logo.svg";

interface FormField {
  label: string;
  type: string;
  name: string;
  validation?: Record<string, any>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showSuggestions?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  labelExtra?: React.ReactNode;
}

const Register: React.FC = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange', // Validation en temps réel
  });
  const navigate = useNavigate();
  const { setCurrentUser } = useAuth();
  const location = useLocation();
  const [apiError, setApiError] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);

  // Surveiller le mot de passe en temps réel
  const password = watch("password") || "";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const signature = params.get("signature");

    api
      .get(`/auth/register?signature=${signature}`)
      .then((response) => {
        setValue("signature", signature);
      })
      .catch((error) => {
        console.error("Error verifying signature", error);
        setApiError(true);
      });
  }, [location.search, setValue]);

  const onSubmit = async (data: any) => {
    data.firstName = data.firstName.trim();
    data.lastName = data.lastName.trim();

    // Validation des champs d'adresse séparés
    if (!data.streetAddress || !data.postalCode || !data.city) {
      alert('Veuillez remplir tous les champs d\'adresse (adresse, code postal, ville)');
      return;
    }

    // Validation du code postal (5 chiffres)
    const cleanPostalCode = data.postalCode.replace(/\s/g, '');
    if (!/^\d{5}$/.test(cleanPostalCode)) {
      alert('Le code postal doit contenir exactement 5 chiffres');
      return;
    }

    // Construire l'adresse complète pour le backend (format attendu pour compatibilité)
    data.address = `${data.streetAddress.trim()} ${cleanPostalCode} ${data.city.trim()}`;
    
    // Envoyer aussi les champs séparés pour que le backend puisse les utiliser directement
    data.streetAddress = data.streetAddress.trim();
    data.postalCode = cleanPostalCode;
    data.city = data.city.trim();

    const urlParams = new URLSearchParams(location.search);
    const sector = urlParams.get("sector");
    const seller_type = urlParams.get("seller_type");

    data.sector = sector;
    data.seller_type = seller_type;

    try {
      const response = await authService.register(data);
      setCurrentUser(response.user);
      navigate("/default");
    } catch (error) {
      console.error("Registration failed", error);
    }
  };

  const handleAddressChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const query = e.target.value;
    setValue("streetAddress", query);
    if (query.length > 2) {
      try {
        const response = await axios.get(
          `https://api-adresse.data.gouv.fr/search/?q=${query}`
        );
        setAddressSuggestions(response.data.features);
      } catch (error) {
        console.error("Error fetching address suggestions", error);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const handleAddressSelect = (suggestion: any) => {
    const props = suggestion.properties;
    // Extraire l'adresse, le code postal et la ville depuis l'API
    setValue("streetAddress", props.name || props.housenumber + ' ' + props.street || props.label.split(',')[0]);
    setValue("postalCode", props.postcode || '');
    setValue("city", props.city || props.context?.split(',')[0] || '');
    setAddressSuggestions([]);
  };

  const inputClass = (error: any) =>
    `w-full px-3 py-2 border rounded ${error ? "border-red-500" : ""}`;

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="w-full flex min-h-screen overflow-hidden">
        {/* Desktop version of the left section */}
        <div
          className="hidden lg:flex p-16 flex-col justify-between items-start"
          style={{
            width: "35%",
            backgroundColor: "#cadcfa",
            height: "100vh",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <img
            src={Logo}
            style={{ marginLeft: 15 }}
            alt="Logo"
            className="h-8"
          />
          <img
            src={require("../../../common/assets/signup-illu.png")}
            style={{
              marginLeft: "auto",
              position: "absolute",
              width: "90%",
              bottom: 0,
              right: 0,
            }}
            alt="Logo"
          />
        </div>

        {/* Mobile and tablet version */}
        <div
          className="flex flex-col w-full lg:pr-72 lg:w-2/3 overflow-y-auto px-6 py-8 sm:px-8 lg:px-28 lg:py-16"
          style={{
            height: "100vh",
          }}
        >
          <div className="lg:hidden mb-6 flex justify-center">
            <img src={Logo} alt="Logo" className="h-8" />
          </div>
          {apiError ? (
            <div className="w-full h-screen flex items-center">
              <p className="text-gray-700 text-center font-semibold">
                Ce lien a déjà été utilisé ou est expiré.
                <br /> Veuillez contacter Fideliz pour plus d'informations.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6 lg:mb-9 text-center lg:text-left">
                Créez votre compte pro et boostez votre CA avec notre solution
                personnalisée !
              </h2>
              <p className="mb-4 lg:mb-7 text-sm text-center lg:text-left">
                * L'ensemble des champs sont obligatoires
              </p>
              <div
                className="flex justify-center items-center mb-6 lg:mb-10 py-4 lg:py-5 rounded-lg"
                style={{ backgroundColor: "#FAF9F8" }}
              >
                <p className="text-sm">
                  Déjà un compte ?{" "}
                  <a href="/login" className="font-bold underline">
                    Connectez-vous
                  </a>
                </p>
              </div>
              {/* Champs d'adresse séparés */}
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse *
                </label>
                <input
                  type="text"
                  {...register("streetAddress", { required: "L'adresse est requise" })}
                  onChange={handleAddressChange}
                  className={`w-full px-3 py-2 border rounded ${errors.streetAddress ? "border-red-500" : ""}`}
                  placeholder="12 rue de la Paix"
                />
                {errors.streetAddress && (
                  <p className="text-red-500 text-xs mt-1">
                    {typeof errors.streetAddress === 'object' && errors.streetAddress?.message
                      ? String(errors.streetAddress.message)
                      : 'Ce champ est requis'}
                  </p>
                )}
                {addressSuggestions && addressSuggestions.length > 0 && (
                  <ul className="bg-white border border-gray-300 rounded mt-2 max-h-40 overflow-auto">
                    {addressSuggestions.map((suggestion) => (
                      <li
                        key={suggestion.properties.id}
                        className="p-2 cursor-pointer hover:bg-gray-200"
                        onClick={() => handleAddressSelect(suggestion)}
                      >
                        {suggestion.properties.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code postal *
                  </label>
                  <input
                    type="text"
                    {...register("postalCode", {
                      required: "Le code postal est requis",
                      pattern: {
                        value: /^\d{5}$/,
                        message: "Le code postal doit contenir exactement 5 chiffres",
                      },
                    })}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                      setValue("postalCode", value);
                    }}
                    className={`w-full px-3 py-2 border rounded ${errors.postalCode ? "border-red-500" : ""}`}
                    placeholder="75002"
                    maxLength={5}
                    pattern="[0-9]*"
                    inputMode="numeric"
                  />
                  {errors.postalCode && (
                    <p className="text-red-500 text-xs mt-1">
                      {typeof errors.postalCode === 'object' && errors.postalCode?.message
                        ? String(errors.postalCode.message)
                        : 'Ce champ est requis'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville *
                  </label>
                  <input
                    type="text"
                    {...register("city", { required: "La ville est requise" })}
                    className={`w-full px-3 py-2 border rounded ${errors.city ? "border-red-500" : ""}`}
                    placeholder="Paris"
                  />
                  {errors.city && (
                    <p className="text-red-500 text-xs mt-1">
                      {typeof errors.city === 'object' && errors.city?.message
                        ? String(errors.city.message)
                        : 'Ce champ est requis'}
                    </p>
                  )}
                </div>
              </div>

              <AuthForm
                onSubmit={onSubmit}
                register={register}
                handleSubmit={handleSubmit}
                watch={watch}
                isSubmitting={isSubmitting}
                errors={errors}
                passwordStrengthIndicator={<PasswordStrengthIndicator password={password} />}
                additionalDisabled={!watch("streetAddress") || !watch("postalCode") || !watch("city")}
                fields={[
                  {
                    label: "Prénom",
                    type: "text",
                    name: "firstName",
                    validation: {
                      required: "Le prénom est requis",
                      pattern: {
                        value: /^[A-Za-zÀ-ÿ '-]+$/,
                        message: "Le prénom ne peut pas contenir de chiffres",
                      },
                    },
                  },
                  {
                    label: "Nom",
                    type: "text",
                    name: "lastName",
                    validation: {
                      required: "Le nom est requis",
                      pattern: {
                        value: /^[A-Za-zÀ-ÿ '-]+$/,
                        message: "Le nom ne peut pas contenir de chiffres",
                      },
                    },
                  },
                  {
                    label: "Nom commercial de votre boutique",
                    type: "text",
                    name: "opticianName",
                    validation: {
                      required: "Le nom commercial de votre boutique est requis",
                    },
                  },
                  {
                    label: "Email",
                    type: "email",
                    name: "email",
                    validation: {
                      required: "L'email est requis",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Adresse email invalide",
                      },
                    },
                  },
                  {
                    label: "Mot de passe",
                    type: "password",
                    name: "password",
                    validation: {
                      required: "Le mot de passe est requis",
                      minLength: {
                        value: 8,
                        message: "Le mot de passe doit comporter au moins 8 caractères",
                      },
                      validate: {
                        hasUpperCase: (value: string) =>
                          /[A-Z]/.test(value) || "Le mot de passe doit contenir au moins une majuscule",
                        hasNumber: (value: string) =>
                          /[0-9]/.test(value) || "Le mot de passe doit contenir au moins un chiffre",
                        hasSpecialChar: (value: string) =>
                          /[!@#$%^&*(),.?":{}|<>]/.test(value) || "Le mot de passe doit contenir au moins un caractère spécial",
                      },
                    },
                  },
                  {
                    label: "Confirmer le mot de passe",
                    type: "password",
                    name: "confirmPassword",
                    validation: {
                      required: "La confirmation du mot de passe est requise",
                      validate: (value: string) =>
                        value === watch("password") ||
                        "Les mots de passe ne correspondent pas",
                    },
                  },
                  {
                    label: "Téléphone personnel",
                    type: "tel",
                    name: "phone",
                    validation: {
                      required: "Le numéro de téléphone est requis",
                      pattern: {
                        value: /^0\d{9}$/,
                        message:
                          "Le numéro de téléphone doit commencer par 0 et contenir exactement 10 chiffres",
                      },
                      maxLength: {
                        value: 10,
                        message:
                          "Le numéro de téléphone doit contenir exactement 10 chiffres",
                      },
                    },
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      let value = e.target.value.replace(/\D/g, ""); // Retirer les caractères non numériques
                      if (value.length > 10) {
                        value = value.slice(0, 10); // Limiter à 10 caractères
                      }
                      setValue("phone", value); // Mettre à jour la valeur du champ
                    },
                    inputProps: {
                      maxLength: 10, // Empêcher la saisie de plus de 10 caractères
                      pattern: "[0-9]*", // Restreindre l'entrée aux chiffres uniquement
                      inputMode: "numeric", // Clavier numérique sur les mobiles
                    },
                  },
                  {
                    label: "Téléphone de l'entreprise",
                    type: "tel",
                    name: "businessPhone",
                    validation: {
                      required:
                        "Le numéro de téléphone de l'entreprise est requis",
                      pattern: {
                        value: /^0\d{9}$/,
                        message:
                          "Le numéro de téléphone doit commencer par 0 et contenir exactement 10 chiffres",
                      },
                      maxLength: {
                        value: 10,
                        message:
                          "Le numéro de téléphone doit contenir exactement 10 chiffres",
                      },
                    },
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      let value = e.target.value.replace(/\D/g, ""); // Retirer les caractères non numériques
                      if (value.length > 10) {
                        value = value.slice(0, 10); // Limiter à 10 caractères
                      }
                      setValue("businessPhone", value); // Mettre à jour la valeur du champ
                    },
                    inputProps: {
                      maxLength: 10, // Empêcher la saisie de plus de 10 caractères
                      pattern: "[0-9]*", // Restreindre l'entrée aux chiffres uniquement
                      inputMode: "numeric", // Clavier numérique sur les mobiles
                    },
                  },
                  {
                    label: "J'accepte les conditions générales d'utilisation",
                    type: "checkbox",
                    name: "acceptCGU",
                    validation: {
                      required: "Vous devez accepter les CGU pour continuer",
                    },
                    labelExtra: (
                      <a
                        href="https://www.fideliz.fr/cgu-professionnel"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-700 hover:text-gray-900 underline ml-1"
                      >
                        (voir les CGU)
                      </a>
                    ),
                  },
                ]}
                addressSuggestions={addressSuggestions}
                handleAddressSelect={handleAddressSelect}
                submitButtonText="S'inscrire"
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Register;
